/**
 * authErrors: one plain sentence for every Firebase auth error (PRP-MCP-11 item 4).
 *
 * Raw Firebase text ("Firebase: Error (auth/invalid-credential).", "Unable to process
 * request due to missing initial state...") never reaches a user. The Firebase code is
 * written to the console so support can still see what happened.
 */

/** Our code for the storage failure the auth handler page reports as text. */
export const MISSING_INITIAL_STATE = 'auth/missing-initial-state';

/** Exact sentence required by PRP-MCP-11 G4 when the browser blocks the popup. */
export const POPUP_BLOCKED_MESSAGE =
  'Your browser blocked the sign-in window. Allow pop-ups for web.anterotrail.com and try again.';

const DEFAULT_MESSAGE = 'Sign-in did not work. Please try again.';

const MESSAGES: Record<string, string> = {
  'auth/wrong-password': 'That email and password do not match. Check them and try again.',
  'auth/user-not-found': 'That email and password do not match. Check them and try again.',
  'auth/invalid-credential': 'That sign-in did not work. Check your details and try again.',
  // Google's response could not be verified (seen on a tampered or replayed handler return).
  'auth/missing-or-invalid-nonce': 'That sign-in did not work. Check your details and try again.',
  'auth/invalid-idp-response': 'That sign-in did not work. Check your details and try again.',
  'auth/user-mismatch': 'That sign-in did not work. Check your details and try again.',
  'auth/user-token-expired': 'Your sign-in expired. Sign in again.',
  'auth/invalid-user-token': 'Your sign-in expired. Sign in again.',
  'auth/requires-recent-login': 'For your security, sign in again to do that.',
  'auth/admin-restricted-operation': 'This sign-in method is not available. Contact support@anterotrail.com.',
  'auth/invalid-api-key': 'Sign-in is not set up correctly. Contact support@anterotrail.com.',
  'auth/invalid-oauth-client-id': 'Sign-in is not set up correctly. Contact support@anterotrail.com.',
  'auth/invalid-login-credentials': 'That email and password do not match. Check them and try again.',
  'auth/invalid-email': 'That email address does not look right. Check it and try again.',
  'auth/missing-password': 'Enter your password to sign in.',
  'auth/user-disabled': 'This account is turned off. Contact support@anterotrail.com for help.',
  'auth/too-many-requests': 'Too many sign-in attempts. Wait a few minutes and try again.',
  'auth/network-request-failed': 'We could not reach the sign-in service. Check your connection and try again.',
  'auth/popup-closed-by-user': 'The sign-in window closed before sign-in finished. Try again.',
  'auth/cancelled-popup-request': 'Another sign-in window is already open. Finish it or close it, then try again.',
  'auth/popup-blocked': POPUP_BLOCKED_MESSAGE,
  'auth/user-cancelled': 'Sign-in was cancelled. Try again when you are ready.',
  'auth/redirect-cancelled-by-user': 'Sign-in was cancelled. Try again when you are ready.',
  'auth/account-exists-with-different-credential':
    'This email already has an account with a different sign-in method. Sign in the way you did before.',
  'auth/credential-already-in-use':
    'This Google account is already linked to another account. Sign in with that account instead.',
  'auth/email-already-in-use': 'An account with this email already exists. Sign in instead.',
  'auth/weak-password': 'Choose a password with at least 6 characters.',
  [MISSING_INITIAL_STATE]: 'Your browser lost track of the sign-in. Try again.',
  'auth/web-storage-unsupported':
    'Your browser is blocking the storage sign-in needs. Allow cookies for this site and try again.',
  'auth/operation-not-supported-in-this-environment':
    'This browser cannot finish Google sign-in here. Try another browser.',
  'auth/unauthorized-domain': 'Sign-in is not set up for this web address yet. Contact support@anterotrail.com.',
  'auth/operation-not-allowed': 'This sign-in method is not available. Contact support@anterotrail.com.',
  'auth/internal-error': DEFAULT_MESSAGE,
  'auth/timeout': 'Sign-in took too long. Try again.',
};

/** The Firebase error code (`auth/...`), or null when the error is not a Firebase error. */
export function authErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const code = (err as { code?: unknown }).code;
  if (typeof code === 'string' && code.startsWith('auth/')) return code;
  if (isMissingInitialState(err)) return MISSING_INITIAL_STATE;
  return null;
}

/** True for the "missing initial state" storage failure, by code or by Firebase's message text. */
export function isMissingInitialState(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const { code, message } = err as { code?: unknown; message?: unknown };
  if (code === MISSING_INITIAL_STATE) return true;
  return typeof message === 'string' && /missing initial state/i.test(message);
}

/** One plain sentence for a sign-in error. Never returns Firebase's own text. */
export function authErrorMessage(err: unknown): string {
  const code = authErrorCode(err);
  return (code && MESSAGES[code]) || DEFAULT_MESSAGE;
}

/** Logs the Firebase code (not the message, which can carry an email address). */
export function logAuthError(err: unknown, where = 'sign-in'): void {
  // eslint-disable-next-line no-console
  console.error(`[auth] ${where} failed: ${authErrorCode(err) ?? 'unknown-error'}`);
}
